import JSZip from 'jszip'
import {
  DocxPlaceholders,
  DocumentPlaceholders,
  CombinedPlaceholders,
  IframeData,
  IframeWithPlaceholder,
  IframeCaptureResult,
  ProcessSection,
  ProcessData
} from './types'
import { RawDraftContentState } from 'draft-js'
import { DEFAULT_EMBED_HEIGHT } from 'components/spreadsheet/constants/const'
import { MimeType, TemplateData, TemplateHandler } from 'easy-template-x'

const addWhiteBackground = (canvas: OffscreenCanvas): OffscreenCanvas => {
  // Create a new canvas with white background
  const exportCanvas = new OffscreenCanvas(canvas.width, canvas.height)
  const ctx = exportCanvas.getContext('2d') as OffscreenCanvasRenderingContext2D

  if (!ctx) {
    throw new Error('Failed to get canvas context')
  }

  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height)

  // Draw the original chart canvas on top
  ctx.drawImage(canvas, 0, 0, exportCanvas.width, exportCanvas.height)

  return exportCanvas
}

const getChartDimensions = (
  format: string,
  orientation: string,
  aspectRatio?: number,
  columns: number = 1
): { width: number; height: number; scale: number } => {
  const DEFAULT_ASPECT_RATIO = 2 // Chart.js default is 2 (width:height ratio)
  const MARGIN = 1 * 96 // 1 inch margin in pixels at 96 DPI
  const CELL_SPACING = 0.1 * 96 // 0.1 inch cell spacing in pixels at 96 DPI

  let pageWidthPixels: number
  let scale: number

  switch (format) {
    case 'A2':
      pageWidthPixels = orientation === 'portrait' ? 1587.4 : 2110.95
      scale = 1
      break
    case 'A3':
      pageWidthPixels = orientation === 'portrait' ? 1122.51 : 1587.4
      scale = 1.5
      break
    case 'A4':
      pageWidthPixels = orientation === 'portrait' ? 793.7 : 1122.51
      scale = 2
      break
    case 'letter':
      pageWidthPixels = orientation === 'portrait' ? 816 : 1056
      scale = 3
      break
    case 'legal':
      pageWidthPixels = orientation === 'portrait' ? 816 : 1344
      scale = 3
      break
    default:
      pageWidthPixels = orientation === 'portrait' ? 793.7 : 1122.51
      scale = 2
  }

  pageWidthPixels -= MARGIN * 2

  // Calculate height using aspect ratio (Chart.js aspectRatio is width/height)
  const ratio = aspectRatio && aspectRatio > 0 ? aspectRatio : DEFAULT_ASPECT_RATIO
  const height = Math.round(pageWidthPixels / ratio)

  // Account for cell spacing and divide by columns
  if (columns > 1) {
    const totalSpacing = CELL_SPACING * (columns + 1)
    pageWidthPixels = (pageWidthPixels - totalSpacing) / columns
  }

  return { width: pageWidthPixels, height, scale }
}

/** The backend's placeholders for views the browser draws: `{CHART …}` (the default) or `{CUSTOM_VIEW …}`. */
const extractPlaceholdersFromDocx = async (
  docxBuffer: ArrayBuffer,
  kind: 'CHART' | 'CUSTOM_VIEW' = 'CHART'
): Promise<DocxPlaceholders> => {
  const zip = await JSZip.loadAsync(docxBuffer)
  const documentXml = await zip.file('word/document.xml')?.async('string')

  if (!documentXml) {
    return []
  }

  const placeholderRegex = new RegExp(
    `\\{${kind} tableId=([^\\s]+) viewId=([^\\s]+) height=(\\d+) columns=(\\d+) instance=(\\d+)\\}`,
    'g'
  )
  const placeholders: DocxPlaceholders = []
  let match

  while ((match = placeholderRegex.exec(documentXml)) !== null) {
    const [, tableId, viewId, height, columns, instance] = match
    placeholders.push({ tableId, viewId, height: Number(height), columns: Number(columns), instance: Number(instance) })
  }

  return placeholders
}

const extractPlaceholdersFromDocument = async (): Promise<DocumentPlaceholders> => {
  const placeholders: DocumentPlaceholders = []
  const counterTracker: Record<string, number> = {}
  const capturedViewIds = new Set<string>()

  // 1. Find canvas-based charts (bar, pie, line, gantt, etc.)
  const canvasElements = document.querySelectorAll('canvas')
  for (let index = 0; index < canvasElements.length; index++) {
    const canvas = canvasElements[index]
    const canvasWrapper = canvas.parentElement

    if (!canvasWrapper) continue

    const tableId = canvasWrapper.getAttribute('data-table-id')
    const viewId = canvasWrapper.getAttribute('data-view-id')

    if (!tableId || !viewId) continue

    const baseKey = `CHART tableId=${tableId} viewId=${viewId}`
    counterTracker[baseKey] = (counterTracker[baseKey] || 0) + 1
    capturedViewIds.add(`${tableId}-${viewId}`)

    placeholders.push({ tableId, viewId, canvas, instance: counterTracker[baseKey] })
  }

  // 2. Find card chart containers (marked with data-chart-type="card")
  const cardContainers = document.querySelectorAll('[data-chart-type="card"][data-table-id][data-view-id]')
  for (let index = 0; index < cardContainers.length; index++) {
    const container = cardContainers[index]
    const tableId = container.getAttribute('data-table-id')
    const viewId = container.getAttribute('data-view-id')

    if (!tableId || !viewId) continue
    if (capturedViewIds.has(`${tableId}-${viewId}`)) continue

    const baseKey = `CHART tableId=${tableId} viewId=${viewId}`
    counterTracker[baseKey] = (counterTracker[baseKey] || 0) + 1

    placeholders.push({ tableId, viewId, canvas: null, element: container, instance: counterTracker[baseKey] })
  }

  return placeholders
}

// Helper to check if placeholder lists match
const placeholdersMatch = (docxPlaceholders: DocxPlaceholders, documentPlaceholders: DocumentPlaceholders): boolean => {
  if (docxPlaceholders.length !== documentPlaceholders.length) {
    return false
  }

  return docxPlaceholders.every((docxItem) =>
    documentPlaceholders.some(
      (docItem) =>
        docItem.tableId === docxItem.tableId &&
        docItem.viewId === docxItem.viewId &&
        docItem.instance === docxItem.instance
    )
  )
}

// Helper to wait for frontend placeholders to match backend
const waitForPlaceholdersToMatch = async (
  docxPlaceholders: DocxPlaceholders,
  maxAttempts = 30,
  intervalMs = 500
): Promise<CombinedPlaceholders> => {
  const mergePlaceholders = (documentPlaceholders: DocumentPlaceholders): CombinedPlaceholders => {
    return documentPlaceholders.map((docItem) => {
      const docxItem = docxPlaceholders.find(
        (d) => d.tableId === docItem.tableId && d.viewId === docItem.viewId && d.instance === docItem.instance
      )
      return {
        tableId: docItem.tableId,
        viewId: docItem.viewId,
        canvas: docItem.canvas,
        element: docItem.element,
        height: docxItem?.height ?? 0,
        columns: docxItem?.columns ?? 0,
        instance: docItem.instance
      }
    })
  }

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const documentPlaceholders = await extractPlaceholdersFromDocument()

    if (placeholdersMatch(docxPlaceholders, documentPlaceholders)) {
      return mergePlaceholders(documentPlaceholders)
    }

    // Wait before checking again
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }

  return mergePlaceholders(await extractPlaceholdersFromDocument())
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// The backend writes chart, form, Custom View and iframe placeholders as `{CHART …}`, `{FORM …}`, `{CUSTOM_VIEW …}`
// and `{IFRAME …}`.
// easy-template-x treats every `{` and `}` in the file as a tag delimiter, so a brace typed into the
// document, a table cell or the hub's Word template would abort the export. Re-wrap only our own
// placeholders in delimiters that content won't contain, and fill with those, so other braces stay text.
// The formats mirror api/app/services/export/doc.py and the extractor regexes above; FORM placeholders
// get no data, so they render empty as before.
const EXPORT_PLACEHOLDER_DELIMITERS = { tagStart: '{{morta:', tagEnd: ':morta}}' }
const EXPORT_PLACEHOLDER_REGEX =
  /\{((?:CHART|FORM|CUSTOM_VIEW) tableId=[^\s<]+ viewId=[^\s<]+ height=\d+ columns=\d+ instance=\d+|IFRAME src=[^<]+? instance=\d+)\}/g

const fillExportPlaceholders = async (docxBuffer: ArrayBuffer, templateData: TemplateData): Promise<ArrayBuffer> => {
  const zip = await JSZip.loadAsync(docxBuffer)
  const documentXml = await zip.file('word/document.xml')?.async('string')
  if (documentXml) {
    const { tagStart, tagEnd } = EXPORT_PLACEHOLDER_DELIMITERS
    zip.file('word/document.xml', documentXml.replace(EXPORT_PLACEHOLDER_REGEX, `${tagStart}$1${tagEnd}`))
  }
  const marked = await zip.generateAsync({ type: 'arraybuffer' })
  return new TemplateHandler({ delimiters: EXPORT_PLACEHOLDER_DELIMITERS }).process(marked, templateData)
}

const decodeXmlEntities = (str: string): string => {
  // Decode &amp; last to prevent double-unescaping (e.g., &amp;lt; -> &lt; -> <)
  return str
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
}

const extractIframePlaceholdersFromDocx = async (docxBuffer: ArrayBuffer): Promise<Array<string>> => {
  const zip = await JSZip.loadAsync(docxBuffer)
  const documentXml = await zip.file('word/document.xml')?.async('string')

  if (!documentXml) {
    return []
  }

  const placeholderRegex = /\{IFRAME src=(.+?) instance=(\d+)\}/g
  const placeholders: Array<string> = []
  let match

  while ((match = placeholderRegex.exec(documentXml)) !== null) {
    const [fullMatch] = match
    // Decode XML entities so URLs match the frontend's unencoded URLs
    placeholders.push(decodeXmlEntities(fullMatch))
  }

  return placeholders
}

const extractIframesFromContent = (content: RawDraftContentState | null | undefined): IframeData[] => {
  if (!content || !content.blocks || !content.entityMap) {
    return []
  }

  const iframes: IframeData[] = []
  const { blocks, entityMap } = content

  for (const block of blocks) {
    if (block.type === 'atomic' && block.entityRanges && block.entityRanges.length > 0) {
      const entityKey = String(block.entityRanges[0].key)
      const entity = entityMap[entityKey]

      if (entity && entity.type === 'IFRAME') {
        iframes.push({
          src: entity.data?.src || '',
          height: entity.data?.height || DEFAULT_EMBED_HEIGHT,
          blockKey: block.key
        })
      }
    }
  }

  return iframes
}

const extractIframesFromSections = (sections: ProcessSection[]): IframeData[] => {
  const iframes: IframeData[] = []

  for (const section of sections) {
    // Extract from section description
    if (section.description?.content) {
      iframes.push(...extractIframesFromContent(section.description.content))
    }

    // Recursively extract from child sections
    if (section.children && section.children.length > 0) {
      iframes.push(...extractIframesFromSections(section.children))
    }
  }

  return iframes
}

const extractIframesFromDocument = (process: ProcessData): IframeData[] => {
  const iframes: IframeData[] = []

  // Extract from main document description
  if (process.description?.content) {
    iframes.push(...extractIframesFromContent(process.description.content))
  }

  // Extract from all sections recursively
  if (process.children && process.children.length > 0) {
    iframes.push(...extractIframesFromSections(process.children))
  }

  return iframes
}

export interface EmbeddedView {
  tableId: string
  viewId: string
}

// Collect every embedded table view in a draft-js content block, regardless of scroll state.
// Mirrors extractIframesFromContent but targets DASHBOARD entities (every embedded table is one).
const extractViewsFromContent = (content: RawDraftContentState | null | undefined): EmbeddedView[] => {
  if (!content || !content.blocks || !content.entityMap) {
    return []
  }

  const views: EmbeddedView[] = []
  const { blocks, entityMap } = content

  for (const block of blocks) {
    if (block.type === 'atomic' && block.entityRanges && block.entityRanges.length > 0) {
      const entityKey = String(block.entityRanges[0].key)
      const entity = entityMap[entityKey]

      if (!entity) continue

      if (entity.type === 'DASHBOARD' && Array.isArray(entity.data?.views)) {
        for (const view of entity.data.views) {
          if (view?.tableId && view?.viewId) views.push({ tableId: view.tableId, viewId: view.viewId })
        }
      }
    }
  }

  return views
}

const extractViewsFromSections = (sections: ProcessSection[]): EmbeddedView[] => {
  const views: EmbeddedView[] = []

  for (const section of sections) {
    if (section.description?.content) {
      views.push(...extractViewsFromContent(section.description.content))
    }

    if (section.children && section.children.length > 0) {
      views.push(...extractViewsFromSections(section.children))
    }
  }

  return views
}

// Every embedded view in the document (main description + all nested sections), de-duplicated
// by viewId — a view can be embedded multiple times but its computed grid is identical.
const extractEmbeddedViewsFromDocument = (process: ProcessData): EmbeddedView[] => {
  const views: EmbeddedView[] = []

  if (process.description?.content) {
    views.push(...extractViewsFromContent(process.description.content))
  }

  if (process.children && process.children.length > 0) {
    views.push(...extractViewsFromSections(process.children))
  }

  const seen = new Set<string>()

  // Unique set
  return views.filter((view) => {
    if (seen.has(view.viewId)) return false
    seen.add(view.viewId)
    return true
  })
}

const matchIframePlaceholders = (
  docxPlaceholders: string[],
  documentIframes: IframeData[]
): IframeWithPlaceholder[] => {
  const result: IframeWithPlaceholder[] = []
  const counterTracker: Record<string, number> = {}

  for (const iframe of documentIframes) {
    counterTracker[iframe.src] = (counterTracker[iframe.src] || 0) + 1

    const expectedPlaceholder = `{IFRAME src=${iframe.src} instance=${counterTracker[iframe.src]}}`

    if (docxPlaceholders.includes(expectedPlaceholder)) {
      result.push({
        src: iframe.src,
        height: iframe.height,
        placeholderKey: expectedPlaceholder
      })
    }
  }

  return result
}

const prepareIframeTemplateData = async (
  captures: Record<string, IframeCaptureResult>,
  pageFormat: string,
  pageOrientation: string
): Promise<TemplateData> => {
  const templateData: TemplateData = {}

  for (const [placeholderKey, capture] of Object.entries(captures)) {
    if (capture.status !== 'success') continue

    const arrayBuffer = await capture.blob.arrayBuffer()

    // Calculate the actual aspect ratio from captured dimensions, or use default
    const capturedWidth = capture.width || 800
    const capturedHeight = capture.height || 500
    const aspectRatio = capturedWidth / capturedHeight

    // Get base width from page format, then calculate height from actual aspect ratio
    const { width, height } = getChartDimensions(pageFormat, pageOrientation, aspectRatio)

    // Remove curly braces from placeholder key for easy-template-x
    const cleanKey = placeholderKey.replace(/^\{|\}$/g, '')

    templateData[cleanKey] = {
      _type: 'image',
      source: arrayBuffer,
      format: MimeType.Png,
      width,
      height
    }
  }

  return templateData
}

// Card charts are plain DOM, so their view-name heading (Show Title) is not part of
// a Chart.js canvas — the exporter has to redraw it onto the export canvas itself.
// Mirrors the on-screen heading: same text, same vertical centre, same colour, scaled.
const drawCardChartTitle = (
  ctx: CanvasRenderingContext2D,
  container: HTMLElement,
  containerRect: DOMRect,
  canvasWidth: number,
  scaleFactor: number
): void => {
  const titleEl = container.querySelector('[data-card-chart-title]') as HTMLElement | null
  if (!titleEl) {
    return
  }

  const titleText = titleEl.textContent ? titleEl.textContent.trim() : ''
  if (!titleText) {
    return
  }

  const titleStyle = getComputedStyle(titleEl)
  const titleFontSize = Math.round(parseFloat(titleStyle.fontSize) * scaleFactor)
  const titleRect = titleEl.getBoundingClientRect()
  const titleCenterY = (titleRect.top - containerRect.top + titleRect.height / 2) * scaleFactor

  ctx.font = `bold ${titleFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`
  ctx.fillStyle = titleStyle.color || '#1a1a2e'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  // Cap the drawn width so a long view name shrinks to fit rather than bleeding off the page
  ctx.fillText(titleText, canvasWidth / 2, titleCenterY, canvasWidth - 48 * scaleFactor)
}

export {
  addWhiteBackground,
  drawCardChartTitle,
  getChartDimensions,
  extractPlaceholdersFromDocx,
  extractIframePlaceholdersFromDocx,
  extractIframesFromDocument,
  extractEmbeddedViewsFromDocument,
  matchIframePlaceholders,
  prepareIframeTemplateData,
  waitForPlaceholdersToMatch,
  downloadBlob,
  fillExportPlaceholders
}
